/**
 * The one table every Management screen uses.
 *
 * ⛔ WHY THIS EXISTS. Ad health grew search, sorting, resizing, filtering,
 * pagination and bulk actions; Conversion actions and Change history had
 * pagination and nothing else. Three tables on one page behaving three
 * different ways teaches an operator that the controls are decoration —
 * they stop looking for them. Jacob, 2026-08-21: "all the tables in the
 * management page should all be the same."
 *
 * The alternative was pasting Ad health's wiring into each table. That is the
 * shape that produced three copies of one matcher disagreeing with each other
 * earlier this month, so the behaviour lives here once and each table supplies
 * only its columns.
 */
import { Fragment, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FilterBuilder,
  SortableTh,
  TablePagination,
  applyFilterRules,
  useColumnResize,
  usePagination,
  useTableSort,
  type FilterConjunction,
  type FilterRule,
} from '@/components/ui/data-table';
import { filterColumnsFromColumns } from './managementFilter';
import { fuzzyFilter } from '@/lib/fuzzyMatch';

export interface ManagementColumn<T> {
  key: string;
  header: string;
  /** Default width in px. Users can drag from here. */
  width?: number;
  align?: 'left' | 'right';
  /** Extra classes on the desktop table cell (e.g. `align-top` for stacked actions). */
  cellClassName?: string;
  /**
   * Absorb leftover table width. Default: the first column, so the table
   * fills the card instead of leaving a blank strip on the right. Fixed
   * columns keep their pixel width when a neighbour is dragged.
   */
  fill?: boolean;
  /** Omit to make the column unsortable (e.g. an actions column). */
  sort?: (row: T) => string | number;
  /** Value for the filter formula. Defaults to `sort`. Use when sort is a rank. */
  filter?: (row: T) => string | number;
  render: (row: T) => React.ReactNode;
}

export interface ManagementFilter<T> {
  key: string;
  label: string;
  /** Value for a row; rows are kept when it equals the selection. */
  value: (row: T) => string;
}

export interface ManagementBulkAction<T> {
  label: string;
  variant?: 'default' | 'outline' | 'destructive' | 'secondary';
  onRun: (rows: T[]) => void;
  /** Hide the action when the selection cannot accept it. */
  enabledFor?: (rows: T[]) => boolean;
}

export function ManagementTable<T>({
  rows,
  columns,
  getRowId,
  searchFields,
  filters = [],
  bulkActions = [],
  defaultSortKey,
  defaultSortDir = 'desc',
  emptyText,
  noMatchText = 'Nothing matches this search or filter.',
  rowTone,
  searchPlaceholder = 'Search…',
  resizable = true,
}: {
  rows: T[];
  columns: ManagementColumn<T>[];
  getRowId: (row: T) => string;
  /** The row's searchable fields, SEPARATELY (keyword, ad group, campaign...).
   *  Never one joined string: a match must come from a single field. */
  searchFields: (row: T) => ReadonlyArray<string | null | undefined>;
  filters?: ManagementFilter<T>[];
  bulkActions?: ManagementBulkAction<T>[];
  defaultSortKey?: string;
  defaultSortDir?: 'asc' | 'desc';
  emptyText: string;
  noMatchText?: string;
  /** Colour-code a whole row: 'bad' reads as a problem, 'warn' as a caution. */
  rowTone?: (row: T) => 'bad' | 'warn' | '';
  searchPlaceholder?: string;
  /** Column drag-resize. Easy locks widths so Source cannot slide with Decide. */
  resizable?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [conjunction, setConjunction] = useState<FilterConjunction>('and');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Shown when a search change dropped a selection, until the next selection.
  const [selectionCleared, setSelectionCleared] = useState(false);
  // Adjusted during render (not in an effect): a new selection hides the note.
  if (selectionCleared && selected.size > 0) setSelectionCleared(false);

  const widthDefaults = useMemo(() => {
    const out: Record<string, number> = {};
    columns.forEach((c) => {
      out[c.key] = c.width ?? 140;
    });
    return out;
  }, [columns]);

  const { widths, startResize } = useColumnResize(widthDefaults);
  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<T>(
    defaultSortKey,
    defaultSortDir,
  );

  const filterOptions = useMemo(
    () =>
      filters.map((f) => ({
        ...f,
        options: Array.from(
          new Set(rows.map((r) => f.value(r)).filter(Boolean)),
        ).sort(),
      })),
    [filters, rows],
  );

  // ⚠ Derived from EVERY row, not the current page — see managementFilter.ts.
  const filterColumns = useMemo(
    () => filterColumnsFromColumns(columns, rows),
    [columns, rows],
  );

  const filtered = useMemo(() => {
    // Fuzzy search ("mke" finds mikehammermoving.com; lib/fuzzyMatch). Best
    // match first while no column sort is active; a column the user sorts
    // by takes over below.
    // ⛔ Strict when the table has bulk actions (Keyword Review's Pause /
    // Enable selected write to Google Ads): exact / prefix / word / substring
    // only, so nothing loosely similar lands under a bulk write.
    const matched = fuzzyFilter(rows, query, searchFields, {
      sort: !sortKey,
      strict: bulkActions.length > 0,
    });
    const searched = matched.filter((r) => {
      for (const f of filters) {
        const chosen = choices[f.key];
        if (chosen && chosen !== 'all' && f.value(r) !== chosen) return false;
      }
      return true;
    });
    return applyFilterRules(searched, rules, filterColumns, conjunction);
  }, [rows, query, choices, filters, searchFields, rules, filterColumns, conjunction, sortKey, bulkActions.length]);

  const sorted = useMemo(() => {
    const accessors: Record<string, (row: T) => string | number> = {};
    columns.forEach((c) => {
      if (c.sort) accessors[c.key] = c.sort;
    });
    return sortRows(filtered, accessors);
  }, [filtered, sortRows, columns]);

  const { page, pageSize, setPage, setPageSize, pageCount, slice } =
    usePagination(sorted.length);
  const pageRows = slice(sorted);

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(getRowId(r))),
    [rows, selected, getRowId],
  );

  const hasBulk = bulkActions.length > 0;
  const sortableColumns = useMemo(() => columns.filter((c) => c.sort), [columns]);
  const columnCount = columns.length + (hasBulk ? 1 : 0);
  const bulkColWidth = 40;
  const fillKey =
    columns.find((c) => c.fill)?.key ?? columns[0]?.key;
  const minTableWidth =
    (hasBulk ? bulkColWidth : 0) +
    columns.reduce((sum, c) => sum + (widths[c.key] ?? c.width ?? 140), 0);

  const toneClass = (row: T) => {
    const tone = rowTone?.(row);
    if (tone === 'bad') return 'bg-destructive/5';
    if (tone === 'warn') return 'bg-coral-light/30';
    return '';
  };

  return (
    <div className="space-y-3">
      {/* Sticky so search / Filters stay on screen while the Easy tab scrolls. */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 bg-warm-bg py-1">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
              // A new search must not leave rows it now hides selected under a
              // bulk action, so the selection goes, with a note saying so.
              if (selected.size > 0) {
                setSelected(new Set());
                setSelectionCleared(true);
              }
            }}
            placeholder={searchPlaceholder}
            // ⚠ 44px on touch: the default control height is a 32px target,
            // which is under every platform's minimum and fiddly on a phone.
            className="h-11 pl-8 md:h-9"
          />
        </div>
        {filterOptions.map((f) => (
          <Select
            key={f.key}
            value={choices[f.key] ?? 'all'}
            onValueChange={(v) => {
              setChoices((prev) => ({ ...prev, [f.key]: v }));
              setPage(1);
            }}
          >
            <SelectTrigger className="h-11 w-full md:h-9 md:w-[170px]">
              <SelectValue placeholder={f.label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{f.label}: all</SelectItem>
              {f.options.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        {filterColumns.length > 0 ? (
          <FilterBuilder
            columns={filterColumns}
            rules={rules}
            conjunction={conjunction}
            onRulesChange={(next) => {
              setRules(next);
              setPage(1);
            }}
            onConjunctionChange={setConjunction}
            className="h-11 md:h-9"
          />
        ) : null}
      </div>

      {hasBulk && selectionCleared && selected.size === 0 && (
        <p className="text-xs text-muted-foreground" role="status">
          Selection cleared because the search changed.
        </p>
      )}

      {/* ⚠ The bar states the count it will act on. "Pause selected" with no
          number is how someone pauses 40 ads believing they picked 4. */}
      {hasBulk && selectedRows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 p-2">
          <span className="text-xs font-medium">
            {selectedRows.length} selected
          </span>
          {bulkActions
            .filter((a) => a.enabledFor?.(selectedRows) ?? true)
            .map((a) => (
              <Button
                key={a.label}
                size="sm"
                variant={a.variant ?? 'outline'}
                onClick={() => {
                  a.onRun(selectedRows);
                  setSelected(new Set());
                }}
              >
                {a.label}
              </Button>
            ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      {/* ⛔ A SEVEN-COLUMN TABLE IS UNUSABLE ON A PHONE. The container scrolls
          sideways, which is correct and still means an operator swipes through
          Keywords a column at a time. Below `md` the same rows render as cards
          instead — see the block underneath. Sorting, filters, selection and
          pagination are shared state, so both layouts stay in agreement. */}
      <div className="hidden overflow-x-auto md:block">
        <Table
          className="w-full table-fixed"
          style={{ minWidth: minTableWidth }}
          containerClassName="overflow-visible"
        >
          <colgroup>
            {hasBulk ? <col style={{ width: bulkColWidth }} /> : null}
            {columns.map((c) => (
              <col
                key={c.key}
                style={
                  c.key === fillKey
                    ? { minWidth: widths[c.key] }
                    : { width: widths[c.key] }
                }
              />
            ))}
          </colgroup>
          <TableHeader>
            <TableRow>
              {hasBulk && (
                <TableHead className="w-[40px]">
                  <Checkbox
                    aria-label="Select all on this page"
                    checked={
                      pageRows.length > 0 &&
                      pageRows.every((r) => selected.has(getRowId(r)))
                    }
                    onCheckedChange={(v) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        pageRows.forEach((r) =>
                          v ? next.add(getRowId(r)) : next.delete(getRowId(r)),
                        );
                        return next;
                      })
                    }
                  />
                </TableHead>
              )}
              {columns.map((c) => (
                <SortableTh
                  key={c.key}
                  sortKey={c.key}
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={() => (c.sort ? toggleSort(c.key) : undefined)}
                  resizable={resizable}
                  width={widths[c.key]}
                  fill={c.key === fillKey}
                  onResize={resizable ? startResize : undefined}
                  className={c.align === 'right' ? 'text-right' : undefined}
                >
                  {c.header}
                </SortableTh>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="py-6 text-center text-sm text-muted-foreground"
                >
                  {rows.length === 0 ? emptyText : noMatchText}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow key={getRowId(row)} className={toneClass(row)}>
                  {hasBulk && (
                    <TableCell>
                      <Checkbox
                        aria-label="Select row"
                        checked={selected.has(getRowId(row))}
                        onCheckedChange={() =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            const id = getRowId(row);
                            if (next.has(id)) next.delete(id);
                            else next.add(id);
                            return next;
                          })
                        }
                      />
                    </TableCell>
                  )}
                  {columns.map((c) => {
                    const colWidth = widths[c.key];
                    const fills = c.key === fillKey;
                    return (
                    <TableCell
                      key={c.key}
                      className={
                        'overflow-hidden text-xs ' +
                        (c.align === 'right' ? 'text-right tabular-nums ' : '') +
                        (c.cellClassName ?? '')
                      }
                      style={
                        colWidth === undefined
                          ? undefined
                          : fills
                            ? { minWidth: colWidth }
                            : {
                                width: colWidth,
                                minWidth: colWidth,
                                maxWidth: colWidth,
                              }
                      }
                    >
                      {c.render(row)}
                    </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* --- phone layout ------------------------------------------------- */}
      <div className="flex flex-col gap-2 md:hidden">
        {/* Column headers do the sorting on desktop; on a phone there is no
            header row to tap, so sorting needs its own control. */}
        {sortableColumns.length > 0 && (
          <div className="flex items-center gap-2">
            <Select
              value={sortKey ?? ''}
              onValueChange={(v) => v && toggleSort(v)}
            >
              <SelectTrigger className="h-11 flex-1">
                <SelectValue placeholder="Sort by…" />
              </SelectTrigger>
              <SelectContent>
                {sortableColumns.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {c.header}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              className="h-11 shrink-0 px-3"
              onClick={() => sortKey && toggleSort(sortKey)}
              aria-label={
                sortDir === 'desc' ? 'Sorted high to low' : 'Sorted low to high'
              }
            >
              {sortDir === 'desc' ? '↓ High' : '↑ Low'}
            </Button>
          </div>
        )}

        {pageRows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {rows.length === 0 ? emptyText : noMatchText}
          </p>
        ) : (
          pageRows.map((row) => {
            const [lead, ...rest] = columns;
            return (
              <div
                key={getRowId(row)}
                className={
                  'rounded-md border border-border p-3 ' + toneClass(row)
                }
              >
                <div className="flex items-start gap-2">
                  {hasBulk && (
                    <Checkbox
                      aria-label="Select row"
                      className="mt-0.5 h-5 w-5"
                      checked={selected.has(getRowId(row))}
                      onCheckedChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          const id = getRowId(row);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        })
                      }
                    />
                  )}
                  {/* The first column is the row's identity, so it becomes the
                      card's heading rather than another label/value pair. */}
                  <div className="min-w-0 flex-1 break-words text-sm font-medium">
                    {lead ? lead.render(row) : null}
                  </div>
                </div>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  {rest.map((c) => (
                    <Fragment key={c.key}>
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {c.header}
                      </dt>
                      <dd
                        className={
                          'min-w-0 break-words text-xs ' +
                          (c.align === 'right' ? 'tabular-nums' : '')
                        }
                      >
                        {c.render(row)}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
            );
          })
        )}
      </div>

      <TablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        totalRows={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
